import { Link } from 'react-router-dom';
import { ArrowRight, Phone, CheckCircle, Wind, Sparkles, Search } from 'lucide-react';

const PricingPage = () => {
  return (
    <div data-testid="pricing-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-slate-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-teal-600 font-medium text-sm uppercase tracking-wider">
              Transparent Pricing
            </span>
            <h1 className="text-4xl font-bold text-slate-900 mt-3 mb-6">
              Clear, Upfront Prices
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              We believe in honest pricing with no hidden costs. The prices shown are what you'll pay for standard vehicles. If anything differs, we'll explain why before any work begins.
            </p>
          </div>
        </div>
      </section>

      {/* Core Services Pricing */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            Air Conditioning Regas
          </h2>
          <p className="text-slate-600 text-center mb-10 max-w-2xl mx-auto">
            Fixed prices for standard passenger vehicles. No surprises, no hidden extras.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* R134a Regas */}
            <div className="bg-slate-50 rounded-xl p-8 border-2 border-teal-500 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-teal-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                  Most Common
                </span>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                Air Conditioning Regas
              </h3>
              <p className="text-slate-500 text-sm mb-4">R134a Systems</p>
              <div className="text-5xl font-bold text-slate-900 mb-4">
                £79
              </div>
              <p className="text-slate-600 text-sm mb-6">
                Includes full system evacuation, vacuum, refrigerant recharge to manufacturer specification, and basic performance check.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  Full system evacuation
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  Vacuum test
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  Refrigerant recharge to spec
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  Basic performance check
                </li>
              </ul>
              <p className="text-xs text-slate-500 border-t border-slate-200 pt-4">
                Price applies to most standard passenger vehicles. Specialist or commercial vehicles may vary – we'll confirm before any work.
              </p>
            </div>

            {/* R1234yf Regas */}
            <div className="bg-slate-50 rounded-xl p-8 border-2 border-slate-200">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                Air Conditioning Regas
              </h3>
              <p className="text-slate-500 text-sm mb-4">R1234yf Systems</p>
              <div className="text-5xl font-bold text-slate-900 mb-4">
                £129
              </div>
              <p className="text-slate-600 text-sm mb-6">
                Fixed price for vehicles using R1234yf refrigerant. This reflects the higher cost of refrigerant and specialist equipment required.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  Full system evacuation
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  Vacuum test
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  Refrigerant recharge to spec
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  Basic performance check
                </li>
              </ul>
              <p className="text-xs text-slate-500 border-t border-slate-200 pt-4">
                If a system fault is identified prior to regassing, this will be discussed before any further work is carried out.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Optional Add-Ons */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-sm font-medium rounded-full mb-4">
              Optional Add-Ons
            </span>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Optional Services
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              These services can be added to your regas for improved comfort, air quality, and peace of mind. All are entirely optional.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Health Check */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Search className="w-5 h-5 text-teal-600" />
                </div>
                <span className="text-xs text-teal-600 font-medium uppercase">Optional</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">
                AC System Health Check
              </h3>
              <div className="text-3xl font-bold text-slate-900 mb-3">
                £29
              </div>
              <p className="text-slate-600 text-sm mb-4">
                A pre-regas check to identify leaks or system faults before refrigerant is added, helping avoid unnecessary regassing.
              </p>
              <p className="text-xs text-slate-500 border-t border-slate-100 pt-3">
                Fixed price for all vehicles. Recommended if your AC hasn't been serviced recently.
              </p>
            </div>

            {/* Cabin Filter */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Wind className="w-5 h-5 text-teal-600" />
                </div>
                <span className="text-xs text-teal-600 font-medium uppercase">Optional</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">
                Cabin Filter Replacement
              </h3>
              <div className="text-3xl font-bold text-slate-900 mb-3">
                £29–£39
              </div>
              <p className="text-slate-600 text-sm mb-4">
                Replaces the filter that cleans the air entering the cabin, improving airflow and air quality.
              </p>
              <p className="text-xs text-slate-500 border-t border-slate-100 pt-3">
                Final price depends on vehicle model and filter type. We'll confirm the exact cost before fitting.
              </p>
            </div>

            {/* Sanitiser Treatment */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-teal-600" />
                </div>
                <span className="text-xs text-teal-600 font-medium uppercase">Optional</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">
                Sanitiser Treatment
              </h3>
              <div className="text-3xl font-bold text-slate-900 mb-3">
                £29–£39
              </div>
              <p className="text-slate-600 text-sm mb-4">
                Disinfects the air conditioning and ventilation system to reduce odours, bacteria, and mould.
              </p>
              <p className="text-xs text-slate-500 border-t border-slate-100 pt-3">
                Price varies by vehicle size. Recommended if you notice musty smells when the AC is running.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Repairs Note */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 text-center">
              What About Repairs?
            </h2>
            <p className="text-slate-600 text-center mb-8">
              If your air conditioning system has a fault (such as a leak or failed component), repair costs will depend on what parts and labour are needed. Here's how we handle it:
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">We diagnose the fault first and explain exactly what's wrong</span>
              </div>
              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">You receive a clear quote for the repair before we start</span>
              </div>
              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">No repair work is carried out without your approval</span>
              </div>
              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">We use quality parts and provide warranty where applicable</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Price Promise */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-teal-200 rounded-xl p-8 max-w-3xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              Our Pricing Promise
            </h3>
            <p className="text-slate-600 mb-4">
              The prices listed are what you'll pay. If we discover something during a service that means the cost would change, we'll stop and discuss it with you first. You'll never be charged more than agreed without your say-so.
            </p>
            <p className="text-slate-600">
              That's our commitment to honest, transparent pricing.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Book?</h2>
          <p className="text-slate-300 mb-8">
            Get in touch to arrange your air conditioning service.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors"
              data-testid="pricing-cta-contact"
            >
              Book Now
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="tel:01323643333"
              className="inline-flex items-center gap-2 bg-white text-slate-800 px-6 py-3 rounded-lg font-medium hover:bg-slate-100 transition-colors"
            >
              <Phone className="w-4 h-4" />
              01323 643333
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
